import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  newBudgetItemSchema,
  BUDGET_CATEGORIES,
  type NewBudgetItemFormValues,
  type NewBudgetItemFormInput,
} from '@/utils/schemas'

interface BudgetItemFormProps {
  defaultValues?: Partial<NewBudgetItemFormInput>
  onSubmit: (values: NewBudgetItemFormValues) => void
  onCancel?: () => void
  isPending: boolean
  isError: boolean
  submitLabel: string
}

export function BudgetItemForm({
  defaultValues,
  onSubmit,
  onCancel,
  isPending,
  isError,
  submitLabel,
}: BudgetItemFormProps) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewBudgetItemFormInput, unknown, NewBudgetItemFormValues>({
    resolver: zodResolver(newBudgetItemSchema),
    defaultValues,
  })

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-3 rounded-md border border-line-strong bg-panel-2 p-4"
    >
      <input
        aria-label={t('budget.label')}
        placeholder={t('budget.label')}
        className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
        {...register('label')}
      />
      {errors.label && (
        <p role="alert" className="text-sm text-brass-warm">
          {t('budget.labelRequired')}
        </p>
      )}
      <select
        aria-label={t('budget.selectCategory')}
        className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
        {...register('category')}
      >
        <option value="">{t('budget.selectCategory')}</option>
        {BUDGET_CATEGORIES.map((cat) => (
          <option key={cat} value={cat}>
            {t(`budget.category.${cat}`)}
          </option>
        ))}
      </select>
      <div className="grid grid-cols-2 gap-3">
        <input
          type="number"
          step="0.01"
          min="0"
          aria-label={t('budget.plannedAmount')}
          placeholder={t('budget.plannedAmount')}
          className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
          {...register('plannedAmount')}
        />
        <input
          type="number"
          step="0.01"
          min="0"
          aria-label={t('budget.actualAmount')}
          placeholder={t('budget.actualAmount')}
          className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
          {...register('actualAmount')}
        />
      </div>
      {isError && (
        <p role="alert" className="text-sm text-brass-warm">
          {t('budget.saveError')}
        </p>
      )}
      <div className="flex gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory"
          >
            {t('budget.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={isPending}
          className="rounded border border-brass bg-brass px-4 py-2 font-sans text-sm font-medium text-night disabled:opacity-50"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  )
}
